import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  ApolloError,
  Badge,
  Button,
  Divider,
  Heading,
  HStack,
  LoadingIndicator,
  NavPersistedScrollPane as ScrollPane,
  SearchInput,
  Select,
  Spacer,
  useShowMessage,
  VStack,
} from '../../core-ui';
import { useNavigation, useNavPersistedState } from '../Navigation';
import formatDate from '../../core/support/formatDate';
import { useExport } from '../../support/useExport';
import { confirm } from '../App/AppDialog';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';
import { useAuthState } from '../AuthState';

import { DELETE_ACCOUNTS, GET_ACCOUNT_LIST } from './graphql';
import {
  GetAccountList,
  GetAccountList_accounts as Account,
} from './__generated__/GetAccountList';
import {
  DeleteAccounts,
  DeleteAccountsVariables,
} from './__generated__/DeleteAccounts';
import { TableView } from '../../core-ui/TableView';

const getAccountStatusToName = () => ({
  all: t('All accounts'),
  active: t('Active accounts'),
  suspended: t('Suspended accounts'),
});

type AccountStatus = keyof ReturnType<typeof getAccountStatusToName>;

export default function AccountListView() {
  const { navigate } = useNavigation();
  const [exportAccounts] = useExport('/api/accounts/export');

  const auth = useAuthState();
  const myAccountId = auth.account.id;
  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();

  const [selected, setSelected] = useState(() => new Set<string>());

  const [
    accountStatusFilter,
    setAccountStatusFilter,
  ] = useNavPersistedState<AccountStatus>('account-status-filter', 'active');
  const [searchValue, setSearchValue] = useNavPersistedState(
    'search-value',
    '',
  );

  const [deleteAccounts] = useMutation<DeleteAccounts, DeleteAccountsVariables>(
    DELETE_ACCOUNTS,
    {
      onError: showApolloError,
      onCompleted: () => {
        setSelected(new Set());
      },
      refetchQueries: [GET_ACCOUNT_LIST],
    },
  );

  const { error, data } = useQuery<GetAccountList>(GET_ACCOUNT_LIST, {
    fetchPolicy: 'cache-and-network',
  });

  const sortedAccounts = useMemo(() => {
    const accounts = data?.accounts ?? [];
    return accounts.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const filteredAccounts = useMemo(
    () => filterAccounts(sortedAccounts, accountStatusFilter, searchValue),
    [sortedAccounts, accountStatusFilter, searchValue],
  );

  if (error) {
    return <ApolloError error={error} />;
  }

  const isLoading = !data;

  return (
    <VStack px={10} pt={10} flex={1} minH={0} alignItems="stretch" spacing={2}>
      <HStack>
        <Heading as="h2" size="lg">
          {t('Accounts')}
        </Heading>
        <Spacer />
        <Button onClick={() => exportAccounts()}>{t('Export to CSV')}</Button>
        <Button
          onClick={() => {
            navigate('/admin/accounts/new');
          }}
        >
          {t('New Account')}
        </Button>
      </HStack>
      <Divider borderColor="gray.200" />
      <HStack spacing={6}>
        <Select
          width="auto"
          options={
            Object.entries(getAccountStatusToName()) as Array<
              [AccountStatus, string]
            >
          }
          isSelected={([key]) => key === accountStatusFilter}
          getLabel={([_key, label]) => label}
          onChange={([key]) => setAccountStatusFilter(key)}
        />
        <SearchInput
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.target.value);
          }}
          onClear={() => {
            setSearchValue('');
          }}
        />
        {selected.size ? (
          <Button
            onClick={async () => {
              if (selected.has(myAccountId)) {
                showMessage(
                  'error',
                  t("You cannot delete the account you're logged in as"),
                );
                return;
              }
              const proceed = await confirm(
                t('Delete Accounts?'),
                t(
                  'Are you sure you want to permanently delete {count} accounts?',
                  { count: selected.size },
                ),
              );
              if (!proceed) {
                return;
              }
              await deleteAccounts({
                variables: { ids: Array.from(selected) },
              });
              showMessage(
                'success',
                t('Successfully deleted {count} account(s)', {
                  count: selected.size,
                }),
              );
            }}
          >
            {t('Delete Selected')}
          </Button>
        ) : null}
      </HStack>
      <ScrollPane
        storeKey="account-list"
        contentContainerProps={{ flexGrow: 1, pb: 10 }}
      >
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <AccountList
            accounts={filteredAccounts}
            selected={selected}
            onSelectionChange={(selected) => setSelected(selected)}
          />
        )}
      </ScrollPane>
    </VStack>
  );
}

function AccountList(props: {
  accounts: Array<Account>;
  selected: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
}) {
  const { accounts, selected, onSelectionChange } = props;
  const { navigate } = useNavigation();

  const [sortBy, setSortBy] = useNavPersistedState<[number, boolean]>(
    'sort-state',
    // Initially sort by column 0 (account name) ascending
    [0, false],
  );

  return (
    <TableView
      data={accounts}
      selected={selected}
      onSelectionChange={onSelectionChange}
      onRowClick={(account) => {
        navigate(`/admin/accounts/:id`, { id: account.id });
      }}
      sortBy={sortBy}
      onSortChange={setSortBy}
      columns={[
        {
          title: t('Account Name'),
          render: (account) => (
            <>
              {account.name}
              {account.isSystemAdmin && (
                <Badge mx={2}>{t('System Admin')}</Badge>
              )}
            </>
          ),
          tdProps: { maxWidth: 300 },
          sortable: { sortFn: (a, b) => a.name.localeCompare(b.name) },
        },
        {
          title: t('Email'),
          render: (account) => account.email,
          tdProps: { maxWidth: 300 },
          sortable: { sortFn: (a, b) => a.email.localeCompare(b.email) },
        },
        {
          title: t('Date Created'),
          render: (account) => formatDate(account.createdAt),
          sortable: {
            sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
          },
        },
        {
          title: t('Vendors'),
          render: (account) => account.currentSeason.vendorCount,
          sortable: {
            sortFn: (a, b) =>
              a.currentSeason.vendorCount - b.currentSeason.vendorCount,
          },
        },
        {
          title: t('Markets'),
          render: (account) => account.currentSeason.marketCount,
          sortable: {
            sortFn: (a, b) =>
              a.currentSeason.marketCount - b.currentSeason.marketCount,
          },
        },
        {
          title: t('Status'),
          render: (account) =>
            account.suspended ? (
              <Badge mx={2} colorScheme="yellow">
                {t('Suspended')}
              </Badge>
            ) : (
              <Badge mx={2} colorScheme="green">
                {t('Active')}
              </Badge>
            ),
          sortable: {
            sortFn: (a, b) => Number(a.suspended) - Number(b.suspended),
          },
        },
      ]}
    />
  );
}

function filterAccounts(
  accounts: Array<Account>,
  accountStatus: AccountStatus,
  searchValue: string,
) {
  const searchText = searchValue.toLowerCase().trim();
  return accounts.filter(
    (account) =>
      accountStatusMatches(account, accountStatus) &&
      accountSearchMatches(account, searchText),
  );
}

function accountSearchMatches(account: Account, searchText: string) {
  if (searchText === '') {
    return true;
  }
  for (const key of ['name', 'email', 'id'] as const) {
    const value = typeof account[key] === 'string' ? account[key] : '';
    if (value.toLowerCase().includes(searchText)) {
      return true;
    }
  }
  return false;
}

function accountStatusMatches(account: Account, accountStatus: AccountStatus) {
  if (accountStatus === 'active') {
    return account.suspended === false;
  } else if (accountStatus === 'suspended') {
    return account.suspended === true;
  } else {
    return true;
  }
}
